import { isNil } from 'lodash';
import { useMemo, type ComponentProps } from 'react';

import {
  borrowerHighInfoData,
  borrowerOnboardingLoanInfoData,
  borrowerOnboardingInfoData,
  borrowerTuayInfo,
  borrowerUnemploymentInfo,
  borrowerSpouseInfo,
  borrowerSpouseUnemploymentInfo,
  borrowerPaidLoanDetailData,
  borrowerLoanDetailData,
  borrowerLoanSummaryData,
  borrowerIDRFormSummaryData,
  borrowerPSLFFormSummaryData,
  borrowerRecommendPlansData,
  borrowerSaveTransitionData,
  type SaveTransitionBorrower,
} from 'utils/borrower';
import {
  LOAN_TYPE_FSA,
  LOAN_TYPE_PLAID_FEDERAL,
  LOAN_TYPE_PLAID_PRIVATE,
} from 'utils/constants';
import BorrowerHighInfoDisplay from 'views/Borrower/BorrowerHighInfo';
import BorrowerIDRFormSummaryTable from 'views/Borrower/BorrowerIDRFormSummaryTable';
import BorrowerFiles from 'views/Borrower/BorrowerFiles';
import BorrowerIDRInfoDisplay from 'views/Borrower/BorrowerIDRInfo';
import BorrowerLoanDetailsTable from 'views/Borrower/BorrowerLoanDetailsTable';
import BorrowerLoanSummaryDisplay from 'views/Borrower/BorrowerLoanSummary';
import BorrowerOnboardingInfoDisplay from 'views/Borrower/BorrowerOnboardingInfo';
import BorrowerPSLFFormSummaryTable from 'views/Borrower/BorrowerPSLFFormSummaryTable';
import BorrowerRecommendedPlansDisplay from 'views/Borrower/BorrowerRecommendPlans';
import BorrowerSaveTransition from 'views/Borrower/BorrowerSaveTransition';
import EmailUpdateHistory from 'views/Borrower/EmailUpdateHistory';
import HouseholdSharing, {
  type HouseholdSharingData,
} from 'views/Borrower/HouseholdSharing';
import LegalProfile, {
  type LegalProfilePresence,
} from 'views/Borrower/LegalProfile';
import PhoneNumberUpdateHistory, {
  type PhoneNumberHistoryBorrower,
} from 'views/Borrower/PhoneNumberUpdateHistory';

type HighInfoBorrower = Parameters<typeof borrowerHighInfoData>[0]['borrower'];

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- borrower is any until fetchBorrower's response is typed
const BorrowerData = ({ borrower, throttleCheckedAt }: any) => {
  const memoizedHighInfoData = useMemo(
    () =>
      borrowerHighInfoData({
        borrower: borrower as HighInfoBorrower,
        throttleCheckedAt: throttleCheckedAt as number | undefined,
      }),
    [borrower, throttleCheckedAt],
  );
  const memoizedOnboardingLoanInfo = useMemo(
    () => borrowerOnboardingLoanInfoData({ borrower }),
    [borrower],
  );

  const memoizedOnboardingBorrowerInfo = useMemo(
    () => borrowerOnboardingInfoData({ borrower }),
    [borrower],
  );
  const memoizedFSALoanSummaryData = useMemo(
    () => borrowerLoanSummaryData({ borrower }, LOAN_TYPE_FSA),
    [borrower],
  );
  const memoizedFSAPaidLoanInfo = useMemo(
    () => borrowerPaidLoanDetailData({ borrower }, LOAN_TYPE_FSA),
    [borrower],
  );
  const memoizedFSALoanInfo = useMemo(
    () => borrowerLoanDetailData({ borrower }, LOAN_TYPE_FSA),
    [borrower],
  );
  const memoizedPlaidFederalLoanSummaryData = useMemo(
    () => borrowerLoanSummaryData({ borrower }, LOAN_TYPE_PLAID_FEDERAL),
    [borrower],
  );
  const memoizedPlaidPrivateLoanSummaryData = useMemo(
    () => borrowerLoanSummaryData({ borrower }, LOAN_TYPE_PLAID_PRIVATE),
    [borrower],
  );
  const memoizedPlaidFederalPaidLoanInfo = useMemo(
    () => borrowerPaidLoanDetailData({ borrower }, LOAN_TYPE_PLAID_FEDERAL),
    [borrower],
  );
  const memoizedPlaidPrivatePaidLoanInfo = useMemo(
    () => borrowerPaidLoanDetailData({ borrower }, LOAN_TYPE_PLAID_PRIVATE),
    [borrower],
  );
  const memoizedPlaidFederalLoanInfo = useMemo(
    () => borrowerLoanDetailData({ borrower }, LOAN_TYPE_PLAID_FEDERAL),
    [borrower],
  );

  const memoizedPlaidPrivateLoanInfo = useMemo(
    () => borrowerLoanDetailData({ borrower }, LOAN_TYPE_PLAID_PRIVATE),
    [borrower],
  );

  const memoizedBorrowerTuayInfo = useMemo(
    () => !isNil(borrower.financialProfile) && borrowerTuayInfo({ borrower }),
    [borrower],
  );

  const memoizedBorrowerUnemploymentInfo = useMemo(
    () =>
      !isNil(borrower.financialProfile) &&
      borrowerUnemploymentInfo({ borrower }),
    [borrower],
  );
  const memoizedBorrowerSpouseInfo = useMemo(
    () => !isNil(borrower.financialProfile) && borrowerSpouseInfo({ borrower }),
    [borrower],
  );

  const memoizedSpouseUnemploymentInfo = useMemo(
    () =>
      !isNil(borrower.financialProfile) &&
      borrowerSpouseUnemploymentInfo({ borrower }),
    [borrower],
  );

  const memoizedBorrowerHasFinancialProfile = useMemo(() => {
    return borrower.financialProfile;
  }, [borrower]);
  const memoizedBorrowerIDRForms = useMemo(
    () => borrowerIDRFormSummaryData({ borrower }),
    [borrower],
  );
  const memoizedBorrowerPSLFForms = useMemo(
    () => borrowerPSLFFormSummaryData({ borrower }),
    [borrower],
  );

  const memoizedBorrowerRecommendedPlansData = useMemo(() => {
    return borrowerRecommendPlansData({ borrower });
  }, [borrower]);

  const memoizedBorrowerSaveTransitionData = useMemo(() => {
    return borrowerSaveTransitionData({
      borrower: borrower as SaveTransitionBorrower,
    });
  }, [borrower]);

  return (
    <>
      <BorrowerHighInfoDisplay
        borrower={borrower}
        data={memoizedHighInfoData}
      />
      <PhoneNumberUpdateHistory
        borrower={borrower as PhoneNumberHistoryBorrower}
      />
      <EmailUpdateHistory
        emailChanges={
          (borrower as ComponentProps<typeof EmailUpdateHistory>).emailChanges
        }
      />
      <LegalProfile
        legalProfile={
          (borrower as { legalProfile?: LegalProfilePresence }).legalProfile
        }
      />
      <BorrowerOnboardingInfoDisplay
        loanInfo={memoizedOnboardingLoanInfo}
        borrowerInfo={memoizedOnboardingBorrowerInfo}
      />
      <BorrowerFiles />
      <BorrowerLoanSummaryDisplay
        borrowerInfo={memoizedFSALoanSummaryData}
        type={LOAN_TYPE_FSA}
      />
      <BorrowerLoanDetailsTable
        paidLoanInfo={memoizedFSAPaidLoanInfo}
        activeloanInfo={memoizedFSALoanInfo}
        type={LOAN_TYPE_FSA}
      />
      <BorrowerLoanSummaryDisplay
        borrowerInfo={memoizedPlaidFederalLoanSummaryData}
        type={LOAN_TYPE_PLAID_FEDERAL}
      />
      <BorrowerLoanDetailsTable
        paidLoanInfo={memoizedPlaidFederalPaidLoanInfo}
        activeloanInfo={memoizedPlaidFederalLoanInfo}
        type={LOAN_TYPE_PLAID_FEDERAL}
      />
      <BorrowerLoanSummaryDisplay
        borrowerInfo={memoizedPlaidPrivateLoanSummaryData}
        type={LOAN_TYPE_PLAID_PRIVATE}
      />

      <BorrowerLoanDetailsTable
        paidLoanInfo={memoizedPlaidPrivatePaidLoanInfo}
        activeloanInfo={memoizedPlaidPrivateLoanInfo}
        type={LOAN_TYPE_PLAID_PRIVATE}
      />
      <BorrowerIDRInfoDisplay
        borrowerHasFinancialProfile={memoizedBorrowerHasFinancialProfile}
        tuayInfo={memoizedBorrowerTuayInfo}
        unemploymentInfo={memoizedBorrowerUnemploymentInfo}
        spouseInfo={memoizedBorrowerSpouseInfo}
        spouseUnemploymentInfo={memoizedSpouseUnemploymentInfo}
      />
      <BorrowerSaveTransition data={memoizedBorrowerSaveTransitionData} />
      <BorrowerRecommendedPlansDisplay
        recommendedPlans={memoizedBorrowerRecommendedPlansData}
      />
      {memoizedBorrowerIDRForms.length > 0 && (
        <BorrowerIDRFormSummaryTable forms={memoizedBorrowerIDRForms} />
      )}
      {memoizedBorrowerPSLFForms.length > 0 && (
        <BorrowerPSLFFormSummaryTable forms={memoizedBorrowerPSLFForms} />
      )}
      <HouseholdSharing
        householdSharing={
          (borrower as { householdSharing?: HouseholdSharingData })
            .householdSharing
        }
      />
    </>
  );
};

export default BorrowerData;
